import api, { ApiResponse } from './api';

export type CreativeType = 'image' | 'video' | 'audio';
export type CreativeSource = 'upload' | 'studio_export' | 'fb_import';

export interface Creative {
  id: string;
  workspace_id: string;
  name: string;
  type: CreativeType;
  gcs_path: string;
  thumbnail_path: string | null;
  original_filename: string | null;
  mime_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  fb_image_hash: string | null;
  fb_video_id: string | null;
  tags: string[];
  source: CreativeSource;
  source_ref: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Hydrated server-side
  signed_url?: string;
  thumbnail_url?: string | null;
}

export interface CreativeListFilters {
  type?: CreativeType;
  source?: CreativeSource;
  search?: string;
  tags?: string[];
  offset?: number;
  limit?: number;
}

export interface CreativeListResult {
  data: Creative[];
  total: number;
}

export const creativesService = {
  /**
   * Library list - returns active (non-deleted) creatives with hydrated
   * signed URLs.
   */
  list: async (filters: CreativeListFilters = {}): Promise<CreativeListResult> => {
    const params: Record<string, string | number> = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 50,
    };
    if (filters.type) params.type = filters.type;
    if (filters.source) params.source = filters.source;
    if (filters.search) params.search = filters.search;
    if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');

    const response = await api.get<ApiResponse<Creative[]> & { _meta?: { total: number } }>(
      '/creatives',
      { params }
    );
    return {
      data: response.data.data || [],
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<Creative> => {
    const response = await api.get<ApiResponse<Creative>>(`/creatives/${id}`);
    return response.data.data!;
  },

  /**
   * Upload a new creative to the library.
   * For files > 30MB, uses signed URL upload to bypass Firebase proxy limits.
   */
  upload: async (
    file: File,
    options: { workspaceId: string; name?: string; tags?: string[]; source?: CreativeSource; sourceRef?: string; onProgress?: (pct: number) => void }
  ): Promise<Creative> => {
    const DIRECT_UPLOAD_LIMIT = 30 * 1024 * 1024; // 30MB

    if (file.size > DIRECT_UPLOAD_LIMIT) {
      // Large file: use signed URL upload
      const { data } = await api.post<ApiResponse<{ uploadUrl: string; creative: Creative }>>('/creatives/upload-url', {
        filename: file.name,
        contentType: file.type,
        workspace_id: options.workspaceId,
        name: options.name,
        tags: options.tags?.join(','),
      });
      const { uploadUrl, creative } = data.data!;

      // Upload directly to GCS
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && options.onProgress) {
            options.onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      return creative;
    }

    // Small file: direct multipart upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workspace_id', options.workspaceId);
    if (options.name) formData.append('name', options.name);
    if (options.tags && options.tags.length > 0) formData.append('tags', options.tags.join(','));
    if (options.source) formData.append('source', options.source);
    if (options.sourceRef) formData.append('source_ref', options.sourceRef);

    const response = await api.post<ApiResponse<Creative>>('/creatives', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  update: async (id: string, data: { name?: string; tags?: string[] }): Promise<Creative> => {
    const response = await api.patch<ApiResponse<Creative>>(`/creatives/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/creatives/${id}`);
  },

  getDownloadUrl: async (id: string): Promise<string> => {
    const response = await api.get<ApiResponse<{ downloadUrl: string }>>(`/creatives/${id}/download`);
    return response.data.data!.downloadUrl;
  },
};
